import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import NoControlsVideo from "../NoControlsVideo";
import {
  alphaSrc,
  Chevron,
  CheckIcon,
  StarOutline,
  MotionLines,
  prefersReducedMotion,
} from "../funnel/FunnelScreens";

/**
 * Shared pieces for the website onboarding steps. Styled like the web funnel
 * (FunnelScreens / FunnelAuthPage): every step is a white card on the night
 * sky, Fredoka titles, a full-width gradient CTA, and the same slide-and-
 * stagger motion between steps. The classes live in OnboardingStyles (`.ob-*`)
 * and FunnelScreenStyles (`.fs-*`); OnboardingModal renders both.
 */

export const FREDOKA = { fontFamily: "'Fredoka', 'Inter', sans-serif" };

export const NO = {
  accent: "#296FFB",
  ink: "#162642",
  subtitle: "#6B7892",
  cardStroke: "#E4E9F5",
  track: "#DDE6F2",
  red: "#FF4B4B",
  deepBlue: "#1E4FC2",
};

// ── motion ─────────────────────────────────────────────────────────────────
// The funnel's step slide (direction-aware) with its children revealed one
// after another. A step's direct children that should take part are `Item`s.
export const stepVariants = {
  enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 56 : -56, scale: 0.98 }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06,
      delayChildren: 0.04,
    },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir >= 0 ? -56 : 56,
    scale: 0.98,
    transition: { duration: 0.22, ease: "easeIn" },
  }),
};

export const itemVariants = {
  enter: { opacity: 0, y: 18 },
  center: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// A view that swaps inside a step (choose → camera → preview, or a step's
// loading state). It owns its own initial/animate — a motion child mounted
// after its parent has finished animating would otherwise skip the reveal —
// and staggers its own Items.
export const subVariants = {
  enter: { opacity: 0, y: 12 },
  center: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.06, delayChildren: 0.02 },
  },
  exit: { opacity: 0, y: -10, transition: { duration: 0.18, ease: "easeIn" } },
};

export const Item = ({ children, ...props }) => (
  <motion.div variants={itemVariants} {...props}>
    {children}
  </motion.div>
);

export const SubView = ({ children, ...props }) => (
  <motion.div variants={subVariants} initial="enter" animate="center" exit="exit" {...props}>
    {children}
  </motion.div>
);

// ── type ───────────────────────────────────────────────────────────────────
// Eyebrow + title (with an optional gradient-accented tail) + subtitle, each
// an Item so they stagger in.
export const StepHeading = ({ eyebrow, title, accent, subtitle }) => (
  <>
    {eyebrow && <Item className="fs-eyebrow">{eyebrow}</Item>}
    <motion.h1 variants={itemVariants} className="fs-title">
      {title}
      {accent && (
        <>
          {" "}
          <span className="fs-accent">{accent}</span>
        </>
      )}
    </motion.h1>
    {subtitle && (
      <motion.p variants={itemVariants} className="fs-sub">
        {subtitle}
      </motion.p>
    )}
  </>
);

export const Accent = ({ children }) => <span className="fs-accent">{children}</span>;

// ── mascot ─────────────────────────────────────────────────────────────────
export const PANDA_POSTER = "/assets/pixley-panda-nobg.png";

// Transparent panda clips (converted from the panda_vids bucket): HEVC-alpha
// .mov for WebKit, VP9-alpha .webm elsewhere — alphaSrc picks, as the funnel
// does for Pip.
export const pandaClip = (clip) => alphaSrc(`/assets/panda-clips/${clip}`);

// Kept for anything still rendering a <video> with its own <source>
const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(
  typeof navigator !== "undefined" ? navigator.userAgent : ""
);
export const ClipSource = ({ clip }) =>
  IS_SAFARI ? (
    <source src={`/assets/panda-clips/${clip}.mov`} type='video/mp4; codecs="hvc1"' />
  ) : (
    <source src={`/assets/panda-clips/${clip}.webm`} type="video/webm" />
  );

// The panda at the top of a step, with the funnel's star doodles around it.
// Falls back to the still (floating) if the clip can't load; in-app browsers
// that can't show a <video> get the still through NoControlsVideo's poster.
// `zoom` scales the clip up inside its box for clips whose panda sits small
// in the frame (panda_jumping fills about half the width, leaving room to
// jump); the box takes a top margin so the bigger panda has room.
export const Mascot = ({ clip, size = "default", zoom = 1, className = "" }) => {
  const [failed, setFailed] = useState(false);
  const still = failed || !clip;
  const zoomed = zoom !== 1 && !still;
  const classes = [
    "ob-mascot",
    size === "small" ? "is-small" : "",
    size === "large" ? "is-large" : "",
    still ? "is-still" : "",
    zoomed ? "is-zoomed" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Item className={classes} style={zoomed ? { "--ob-zoom": zoom } : undefined}>
      <StarOutline className="fs-star ob-mascot-star-l" />
      <StarOutline className="fs-star ob-mascot-star-r" />
      <MotionLines className="fs-lines ob-mascot-lines" />
      {still ? (
        <img
          className="ob-mascot-clip"
          src={PANDA_POSTER}
          alt=""
          onError={(e) => {
            e.target.src = "/assets/pixley-panda.png";
          }}
        />
      ) : (
        <NoControlsVideo
          className="ob-mascot-clip"
          src={pandaClip(clip)}
          poster={PANDA_POSTER}
          autoPlay={!prefersReducedMotion()}
          loop
          muted
          preload="auto"
          onError={() => setFailed(true)}
          aria-hidden="true"
        />
      )}
    </Item>
  );
};

// ── buttons ────────────────────────────────────────────────────────────────
export const ContinueButton = ({
  onClick,
  disabled,
  loading,
  label = "Continue",
  type = "button",
  className = "",
}) => (
  <button
    type={type}
    className={`fs-cta ${className}`.trim()}
    onClick={onClick}
    disabled={disabled || loading}
    aria-busy={!!loading}
  >
    {loading ? (
      <span className="fs-cta-spinner" aria-label="Loading" />
    ) : (
      <>
        {label} <Chevron />
      </>
    )}
  </button>
);

export const CtaRow = ({ children, className = "" }) => (
  <Item className={`ob-cta-row ${className}`.trim()}>{children}</Item>
);

export const LinkButton = ({ onClick, children }) => (
  <button type="button" className="ob-link" onClick={onClick}>
    {children}
  </button>
);

// ── choices ────────────────────────────────────────────────────────────────
// One-of-many row (radio), as in the iOS flow's NOSelectableRow.
export const SelectableRow = ({ icon, iconBg, title, subtitle, selected, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={!!selected}
    className={`ob-option${selected ? " is-selected" : ""}`}
    onClick={onClick}
  >
    <span className="ob-option-icon" style={{ background: iconBg || "#E3F0FF" }}>
      {icon}
    </span>
    <span className="ob-option-title">
      {title}
      {subtitle && <span className="ob-option-sub">{subtitle}</span>}
    </span>
    <span className="ob-option-radio" aria-hidden="true">
      <CheckIcon size={14} />
    </span>
  </button>
);

export const CheckBadge = () => (
  <span className="ob-check-badge" aria-hidden="true">
    <CheckIcon size={15} />
  </span>
);

// ── celebration ────────────────────────────────────────────────────────────
const CONFETTI_COLORS = ["#296FFB", "#FFC83D", "#7C5CFF", "#34C759", "#FF6FA5"];

export const Confetti = ({ count = 45 }) => {
  const pieces = useRef(
    Array.from({ length: count }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      dur: 2.2 + Math.random() * 1.6,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      drift: (Math.random() - 0.5) * 160,
      spin: 360 + Math.random() * 360,
    }))
  ).current;
  return (
    <div className="ob-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            "--drift": `${p.drift}px`,
            "--spin": `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
};

// ── loader ─────────────────────────────────────────────────────────────────
// A step's waiting state: the panda at work, a title, rotating messages and
// an indeterminate bar. Rendered inside a SubView so it reveals itself.
export const Loader = ({ clip, title, messages = [], interval = 2600 }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (messages.length < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % messages.length), interval);
    return () => clearInterval(id);
  }, [messages.length, interval]);
  return (
    <SubView className="ob-loader" role="status" aria-live="polite">
      <Mascot clip={clip} size="large" />
      <motion.h1 variants={itemVariants} className="fs-title">
        {title}
      </motion.h1>
      {messages.length > 0 && (
        <Item className="ob-msg">
          <AnimatePresence mode="wait">
            <motion.p
              key={index}
              className="fs-sub"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {messages[index]}
            </motion.p>
          </AnimatePresence>
        </Item>
      )}
      <Item className="ob-progress" aria-hidden="true">
        <span className="ob-progress-fill" />
      </Item>
    </SubView>
  );
};
